import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from "react";

/** What the list scrolls in: the list itself where it is the scroller, else its nearest scrolling ancestor. */
function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * Keeps the row the reader is looking at where it is, while rows above it
 * come and go.
 *
 * Answering a task moves it between groups, so the list reorders under
 * someone working down it: a card removed above the fold takes everything
 * below it up by its own height, and the row they were about to press is no
 * longer under the cursor. Measured at a card and a half.
 *
 * The browser will not do this for us here. Scroll anchoring is on — nothing
 * sets `overflow-anchor: none` — and it still does not fire, because React
 * replaces the nodes it would have anchored to. So the anchor is kept by
 * hand: the topmost visible row as the reader scrolls, put back to the same
 * offset once the new rows have laid out.
 *
 * `rows` is read in the body rather than only depended on, and deliberately
 * so: a value that appears solely in a dependency array is one the lint's
 * autofix strips as extraneous, which leaves the effects never re-running and
 * this hook a no-op that still compiles and still passes its tests.
 */
export function useAnchoredScroll(
  listRef: RefObject<HTMLElement | null>,
  rows: readonly unknown[],
): void {
  const anchor = useRef<{ id: string; offset: number } | null>(null);

  const remember = useCallback(() => {
    const list = listRef.current;
    const scroller = list && scrollerOf(list);
    if (!list || !scroller) return;
    const top = scroller.getBoundingClientRect().top;
    const found = [...list.querySelectorAll<HTMLElement>("[data-task-id]")];
    // The first row still on screen, which is the one a reader reads from.
    const row = found.find((r) => r.getBoundingClientRect().bottom > top + 1);
    const id = row?.dataset["taskId"];
    anchor.current = row && id ? { id, offset: row.getBoundingClientRect().top - top } : null;
  }, [listRef]);

  useEffect(() => {
    // Re-runs as the rows arrive: on the first render there is no data, so
    // nothing is tall enough to scroll and there is no scroller to listen to.
    if (rows.length === 0) return;
    const list = listRef.current;
    const scroller = list && scrollerOf(list);
    if (!scroller) return;
    scroller.addEventListener("scroll", remember, { passive: true });
    remember();
    return () => scroller.removeEventListener("scroll", remember);
  }, [listRef, remember, rows]);

  useLayoutEffect(() => {
    const held = anchor.current;
    const list = listRef.current;
    const scroller = list && scrollerOf(list);
    if (rows.length === 0 || !held || !list || !scroller) return;
    const found = [...list.querySelectorAll<HTMLElement>("[data-task-id]")];
    const row = found.find((r) => r.dataset["taskId"] === held.id);
    // The anchor itself was answered and is gone. Nothing to hold on to, and
    // guessing at a replacement would move the view for a different reason.
    if (!row) return;
    const drift =
      row.getBoundingClientRect().top - scroller.getBoundingClientRect().top - held.offset;
    // A sub-pixel drift is layout noise, and writing `scrollTop` for it would
    // fight smooth scrolling that is still settling.
    if (Math.abs(drift) > 1) scroller.scrollTop += drift;
    // Re-read where the anchor ended up, so a second change arriving before
    // the reader scrolls again is measured from where they are now.
    remember();
  }, [listRef, remember, rows]);
}
